import React, { useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  X,
  Search,
  LayoutTemplate,
  BarChart3,
  PieChart,
  MessageSquare,
  Activity,
  TrendingUp,
  ClipboardList,
  Users,
  FileText,
  LineChart,
  AlertTriangle,
  Workflow,
  Sparkles,
  Check,
  ChevronRight,
  LayoutDashboard,
  FileBarChart,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import type { Theme, ThemeColors } from './types';
import {
  REPORT_TEMPLATES,
  REPORT_TEMPLATE_CATEGORIES,
  REPORT_CATEGORY_META,
  getCategoryTemplateCounts,
  groupTemplatesByCategory,
  cloneTemplateEntities,
  type ReportTemplate,
  type ReportTemplateCategory,
} from './reportTemplates';
import { buildReportPreviewHtmlFromDocument, REPORT_PREVIEW_IFRAME_SANDBOX } from './utils';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-800/80 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

const ICON_MAP: Record<string, LucideIcon> = {
  BarChart3, PieChart, MessageSquare, Activity, TrendingUp,
  ClipboardList, Users, FileText, LineChart, AlertTriangle, Workflow,
};

function sortTemplates(list: ReportTemplate[]): ReportTemplate[] {
  return [...list].sort((a, b) => {
    if (a.variant === 'dashboard' && b.variant !== 'dashboard') return -1;
    if (b.variant === 'dashboard' && a.variant !== 'dashboard') return 1;
    return a.name.localeCompare(b.name);
  });
}

interface ReportTemplatePickerModalProps {
  theme?: Theme;
  onClose: () => void;
  onApply: (template: ReportTemplate) => void;
}

const ReportTemplatePickerModal: React.FC<ReportTemplatePickerModalProps> = ({
  theme = 'dark',
  onClose,
  onApply,
}) => {
  const colors = THEME_CONFIG[theme];
  const categoryCounts = useMemo(() => getCategoryTemplateCounts(), []);

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<ReportTemplateCategory>('Analytics');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewMaximized, setPreviewMaximized] = useState(false);

  const isSearching = search.trim().length > 0;

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return sortTemplates(
      REPORT_TEMPLATES.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q)
      )
    );
  }, [search]);

  const categoryTemplates = useMemo(
    () => sortTemplates(REPORT_TEMPLATES.filter((t) => t.category === activeCategory)),
    [activeCategory]
  );

  const dashboards = useMemo(
    () => categoryTemplates.filter((t) => t.variant === 'dashboard'),
    [categoryTemplates]
  );

  const reports = useMemo(
    () => categoryTemplates.filter((t) => t.variant !== 'dashboard'),
    [categoryTemplates]
  );

  const groupedSearch = useMemo(() => groupTemplatesByCategory(searchResults), [searchResults]);

  const visibleTemplates = isSearching ? searchResults : categoryTemplates;

  useEffect(() => {
    if (visibleTemplates.length > 0 && !visibleTemplates.some((t) => t.id === selectedId)) {
      setSelectedId(visibleTemplates[0].id);
    }
  }, [activeCategory, isSearching, visibleTemplates, selectedId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (previewMaximized) {
          setPreviewMaximized(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, previewMaximized]);

  const selected = REPORT_TEMPLATES.find((t) => t.id === selectedId) ?? null;

  const previewHtml = useMemo(() => {
    if (!selected) return '';
    return buildReportPreviewHtmlFromDocument(selected.htmlTemplate, cloneTemplateEntities(selected));
  }, [selected]);

  const handleApply = () => {
    if (selected) {
      onApply(selected);
      onClose();
    }
  };

  const handleMaximizePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewMaximized(true);
  };

  const handleMinimizePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewMaximized(false);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (previewMaximized) return;
    if (e.target === e.currentTarget) onClose();
  };

  const renderTemplateCard = (tpl: ReportTemplate) => {
    const Icon = ICON_MAP[tpl.icon] ?? FileText;
    const isSelected = selected?.id === tpl.id;
    const isDashboard = tpl.variant === 'dashboard';

    return (
      <button
        key={tpl.id}
        type="button"
        onClick={() => setSelectedId(tpl.id)}
        className={`group text-left rounded-lg border transition-all duration-200 overflow-hidden w-full max-w-[220px] ${
          isSelected
            ? 'border-indigo-500/70 bg-indigo-500/10 ring-1 ring-indigo-500/25 shadow-md shadow-indigo-500/10'
            : `${colors.border} ${colors.hover} hover:border-indigo-500/30`
        }`}
      >
        {isDashboard && (
          <div className={`h-0.5 w-full bg-gradient-to-r ${tpl.gradient}`} />
        )}
        <div className="p-2.5">
          <div className="flex items-start gap-2">
            <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${tpl.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <Icon className="w-3.5 h-3.5 text-white" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 flex-wrap">
                <p className={`text-[10px] font-semibold ${colors.text.primary} truncate`}>{tpl.name}</p>
                {isDashboard && (
                  <span className="text-[7px] px-1 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 font-semibold uppercase border border-indigo-500/25">
                    Dash
                  </span>
                )}
                {isSelected && <Check className="w-3 h-3 text-indigo-400 flex-shrink-0" />}
              </div>
              <p className={`text-[9px] ${colors.text.muted} line-clamp-1 mt-0.5 leading-snug`}>
                {tpl.description}
              </p>
            </div>
          </div>
        </div>
      </button>
    );
  };

  const renderPreviewFrame = (fullScreen = false) => (
    <div className={`flex flex-col ${fullScreen ? 'h-full' : 'flex-1'} ${fullScreen ? '' : 'rounded-xl border border-neutral-700/60 overflow-hidden shadow-2xl bg-neutral-800'}`}>
      <div className={`flex items-center gap-2 px-4 py-2 ${fullScreen ? 'py-2.5' : 'py-2.5'} bg-neutral-800 border-b border-neutral-700/80`}>
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
        </div>
        <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-neutral-900/80 text-[10px] text-neutral-500 text-center truncate">
          report-preview · {selected?.suggestedName}
        </div>
        <button
          type="button"
          onClick={(e) => (fullScreen ? handleMinimizePreview(e) : handleMaximizePreview(e))}
          className="p-1.5 rounded-md hover:bg-neutral-700 text-neutral-400 hover:text-white transition-colors"
          title={fullScreen ? 'Exit fullscreen' : 'Maximize preview'}
        >
          {fullScreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="flex-1 bg-white overflow-hidden min-h-0">
        <iframe
          title={`Preview: ${selected?.name}`}
          srcDoc={previewHtml}
          className="w-full h-full border-0"
          sandbox={REPORT_PREVIEW_IFRAME_SANDBOX}
        />
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center bg-black/75 backdrop-blur-md"
      onClick={handleBackdropClick}
    >
      <div
        className={`w-[80vw] h-[80vh] flex flex-col rounded-2xl border ${colors.border} ${theme === 'dark' ? 'bg-neutral-900' : 'bg-white'} shadow-2xl shadow-black/40 overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex-shrink-0 relative border-b ${colors.border} overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 via-violet-600/5 to-transparent pointer-events-none" />
          <div className="relative px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <LayoutTemplate className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className={`text-base font-semibold ${colors.text.primary}`}>Choose a Template</h2>
                <p className={`text-[11px] ${colors.text.muted}`}>
                  {REPORT_TEMPLATES.length} templates · {REPORT_TEMPLATE_CATEGORIES.length} categories · dashboards & reports
                </p>
              </div>
            </div>
            <div className="relative flex-1 max-w-sm">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${colors.text.muted}`} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates across all categories..."
                className={`w-full pl-9 pr-4 py-2 rounded-lg border text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/40 ${colors.input}`}
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`p-2 rounded-lg ${colors.hover} ${colors.text.muted} hover:text-red-400 transition-colors`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Categories */}
          {!isSearching && (
            <aside className={`w-56 flex-shrink-0 border-r ${colors.border} ${colors.primary} overflow-y-auto`}>
              <div className="p-3">
                <p className={`text-[10px] font-semibold uppercase tracking-widest ${colors.text.muted} px-2 mb-2`}>
                  Categories
                </p>
                <div className="space-y-1">
                  {REPORT_TEMPLATE_CATEGORIES.map((cat) => {
                    const meta = REPORT_CATEGORY_META[cat];
                    const CatIcon = ICON_MAP[meta.icon] ?? FileText;
                    const isActive = activeCategory === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCategory(cat)}
                        className={`w-full text-left rounded-xl p-3 transition-all duration-150 ${
                          isActive
                            ? 'bg-gradient-to-r from-indigo-600/20 to-violet-600/10 border border-indigo-500/40 shadow-sm'
                            : `border border-transparent ${colors.hover}`
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-sm ${isActive ? 'ring-2 ring-indigo-400/30' : ''}`}>
                            <CatIcon className="w-4 h-4 text-white" strokeWidth={1.75} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`text-xs font-semibold truncate ${isActive ? 'text-indigo-400' : colors.text.primary}`}>
                                {meta.label}
                              </p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                isActive ? 'bg-indigo-500/25 text-indigo-300' : `${colors.secondary} ${colors.text.muted}`
                              }`}>
                                {categoryCounts[cat]}
                              </span>
                            </div>
                            <p className={`text-[9px] ${colors.text.muted} line-clamp-1 mt-0.5`}>{meta.description}</p>
                          </div>
                          {isActive && <ChevronRight className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>
          )}

          {/* Template grid */}
          <div className={`${isSearching ? 'w-64' : 'w-60'} flex-shrink-0 border-r ${colors.border} flex flex-col min-h-0 ${colors.background}`}>
            <div className={`px-4 py-3 border-b ${colors.border} ${colors.secondary}`}>
              {isSearching ? (
                <p className={`text-xs font-semibold ${colors.text.primary}`}>
                  Search Results
                  <span className={`font-normal ${colors.text.muted}`}> · {searchResults.length} found</span>
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${REPORT_CATEGORY_META[activeCategory].gradient} flex items-center justify-center`}>
                    {React.createElement(ICON_MAP[REPORT_CATEGORY_META[activeCategory].icon] ?? FileText, {
                      className: 'w-3.5 h-3.5 text-white', strokeWidth: 1.75,
                    })}
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${colors.text.primary}`}>
                      {REPORT_CATEGORY_META[activeCategory].label}
                    </p>
                    <p className={`text-[10px] ${colors.text.muted}`}>
                      {dashboards.length} dashboard · {reports.length} reports
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {isSearching ? (
                searchResults.length === 0 ? (
                  <div className={`text-center py-16 ${colors.text.muted}`}>
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-xs">No templates match your search.</p>
                  </div>
                ) : (
                  REPORT_TEMPLATE_CATEGORIES.map((cat) => {
                    const group = groupedSearch[cat];
                    if (!group.length) return null;
                    return (
                      <div key={cat} className="mb-5">
                        <p className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text.muted} px-1 mb-2 flex items-center gap-1.5`}>
                          <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${REPORT_CATEGORY_META[cat].gradient}`} />
                          {cat} ({group.length})
                        </p>
                        <div className="flex flex-col gap-1.5 items-start">{group.map(renderTemplateCard)}</div>
                      </div>
                    );
                  })
                )
              ) : (
                <div className="space-y-5">
                  {dashboards.length > 0 && (
                    <section>
                      <div className="flex items-center gap-2 px-1 mb-2.5">
                        <LayoutDashboard className="w-3.5 h-3.5 text-indigo-400" />
                        <p className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text.secondary}`}>
                          Category Dashboard
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5 items-start">
                        {dashboards.map(renderTemplateCard)}
                      </div>
                    </section>
                  )}

                  {reports.length > 0 && (
                    <section>
                      <div className="flex items-center gap-2 px-1 mb-2.5">
                        <FileBarChart className="w-3.5 h-3.5 text-indigo-400" />
                        <p className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text.secondary}`}>
                          Report Templates
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5 items-start">
                        {reports.map(renderTemplateCard)}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className={`flex-1 flex flex-col min-w-0 ${theme === 'dark' ? 'bg-neutral-950' : 'bg-gray-50'}`}>
            {selected ? (
              <>
                <div className={`px-5 py-3 border-b ${colors.border} flex items-center justify-between ${colors.primary}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${selected.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                      {React.createElement(ICON_MAP[selected.icon] ?? FileText, {
                        className: 'w-4 h-4 text-white', strokeWidth: 1.75,
                      })}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-semibold ${colors.text.primary} truncate`}>{selected.name}</p>
                        {selected.variant === 'dashboard' && (
                          <span className="text-[9px] px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 font-semibold uppercase">
                            Dashboard
                          </span>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-md ${colors.secondary} ${colors.text.muted}`}>
                          {selected.category}
                        </span>
                      </div>
                      <p className={`text-[11px] ${colors.text.muted} mt-0.5 line-clamp-1`}>{selected.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <button
                      type="button"
                      onClick={handleMaximizePreview}
                      className={`p-1.5 rounded-md border ${colors.border} ${colors.hover} ${colors.text.muted} hover:text-indigo-400 transition-colors`}
                      title="Maximize preview"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                    <span className={`text-[10px] flex items-center gap-1.5 ${colors.text.muted}`}>
                      <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                      {selected.entities.length} entities
                    </span>
                  </div>
                </div>

                <div className="flex-1 p-4 overflow-hidden flex flex-col relative group/preview">
                  {renderPreviewFrame(false)}
                </div>
              </>
            ) : (
              <div className={`flex-1 flex flex-col items-center justify-center ${colors.text.muted} gap-3`}>
                <LayoutTemplate className="w-10 h-10 opacity-20" />
                <p className="text-xs">Select a template to preview</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex-shrink-0 px-6 py-3 border-t ${colors.border} ${colors.primary} flex items-center justify-between`}>
          <p className={`text-[11px] ${colors.text.muted}`}>
            {isSearching
              ? `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} across categories`
              : `${REPORT_CATEGORY_META[activeCategory].label} — ${categoryTemplates.length} templates available`}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg text-xs ${colors.text.secondary} ${colors.hover} border ${colors.border}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!selected}
              className="px-5 py-2 rounded-lg text-xs bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:opacity-40 text-white font-semibold flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all"
            >
              <LayoutTemplate className="w-4 h-4" />
              Use This Template
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen preview overlay */}
      {previewMaximized && selected && (
        <div
          className="fixed inset-0 z-[90] flex flex-col bg-neutral-950"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex-shrink-0 px-5 py-3 border-b border-neutral-800 bg-neutral-900 flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${selected.gradient} flex items-center justify-center flex-shrink-0`}>
                {React.createElement(ICON_MAP[selected.icon] ?? FileText, {
                  className: 'w-4 h-4 text-white', strokeWidth: 1.75,
                })}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{selected.name}</p>
                <p className="text-[11px] text-neutral-400 truncate">{selected.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleApply}
                className="px-4 py-2 rounded-lg text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium flex items-center gap-1.5"
              >
                <LayoutTemplate className="w-3.5 h-3.5" />
                Use Template
              </button>
              <button
                type="button"
                onClick={handleMinimizePreview}
                className="p-2 rounded-lg hover:bg-neutral-800 text-neutral-400 hover:text-white transition-colors"
                title="Exit fullscreen"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 p-3 min-h-0 overflow-hidden">
            {renderPreviewFrame(true)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTemplatePickerModal;
